import { useState } from 'react';
import { StyledLeftTooltip } from 'modules/workflowNodeEditors/EditorBody.styled';
import { ResourceType, StartNodeData, startUtils } from 'modules/workflows';
import { Subtitle2 } from 'ui/theme';
import { useStartNodeContext } from '../StartNodeEditor.context';
import { useGetEntrySourceMetadata, useHandleResourceValueChange } from '../StartNodeEditor.hooks';
import { EntrySourceComponentProps } from '../StartNodeEditor.types';
import { getEventResourceField } from './EventResourceForm.utils';
import {
  EVENT_RESOURCE_FIELD_ID,
  EVENT_RESOURCE_FIELD_READ_ONLY_TOOLTIP,
  EVENT_RESOURCE_FIELD_TRIGGER_SELECT,
  EVENT_RESOURCE_FIELD_VIEW_LIST_LINK,
} from './EventEntrySource.constants';
import { ViewListLink } from '../resourceComponents';

/**
 * The start tile is populated by TRIGGER_METADATA
 *
 * For more information visit the [slab documentation](https://iterable.slab.com/posts/start-tile-editor-components-wlds1ru4#h9hxy-entry-source-mapping)
 */
export const EventResourceForm = ({
  displayValue,
  hasCloningError = false,
}: EntrySourceComponentProps) => {
  const { isReadOnly, nodeData } = useStartNodeContext();
  const entrySourceMetadata = useGetEntrySourceMetadata();
  const {
    selectPlaceholder,
    resourceFieldValue: value,
    resourceType,
    resourceHasLink = false,
    resourceTitle = 'Specify event',
    TriggerForm,
  } = entrySourceMetadata;
  const showLink = resourceHasLink && !!value && startUtils.isResourceValueValid(value);
  const [tooltipContent, setTooltipContent] = useState<React.ReactNode>();

  const isCustomEventResourceType = resourceType === ResourceType.CustomEvent;
  const placeholderValue =
    isCustomEventResourceType && value ? selectPlaceholder : displayValue || selectPlaceholder;

  const EventResourceField = getEventResourceField(resourceType);

  const handleChange = useHandleResourceValueChange();

  return (
    <>
      <div>
        <Subtitle2>{resourceTitle}</Subtitle2>
        <StyledLeftTooltip
          data-test={EVENT_RESOURCE_FIELD_READ_ONLY_TOOLTIP}
          tooltipContent={tooltipContent}
          isEnabled={isReadOnly && !!tooltipContent}
        >
          <EventResourceField
            data-test={EVENT_RESOURCE_FIELD_TRIGGER_SELECT}
            id={EVENT_RESOURCE_FIELD_ID}
            placeholder={placeholderValue}
            isError={hasCloningError && !startUtils.isResourceValueValid(value)}
            nodeData={nodeData ?? ({} as unknown as StartNodeData)}
            {...entrySourceMetadata}
            value={value}
            isStartNode={true}
            onChange={handleChange}
            style={{ wordBreak: 'break-all' }}
            displayValue={displayValue}
            isDisabled={isReadOnly}
            setTooltipContent={setTooltipContent}
          />
        </StyledLeftTooltip>
        {showLink && <ViewListLink id={EVENT_RESOURCE_FIELD_VIEW_LIST_LINK} value={value} />}
      </div>
      {TriggerForm && <TriggerForm />}
    </>
  );
};
